import { IMAGE_ALT } from '../../seo/imageAltText';
import PictureImage from '../../components/Common/PictureImage';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Canonical from '../../seo/Canonical';
import {
  BUSINESS_ADDRESS,
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_TEL,
} from '../../seo/businessContact';

const OFFICE_ADDRESS_LINE = [
  BUSINESS_ADDRESS.streetAddress,
  BUSINESS_ADDRESS.addressLocality,
  BUSINESS_ADDRESS.postalCode,
  'UK',
].join(', ');

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="privacy-policy-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      {/* Hero Header Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-container">
          <div className="privacy-hero-content">
            <div className="privacy-hero-left">
              <PictureImage
                width={2000}
                height={1333}
                src="/Images/privacy-policy-header-uk.webp"
                alt={IMAGE_ALT.privacyHero}
                className="privacy-hero-image"
                loading="eager"
                fetchPriority="high"
              />
            </div>
            <div className="privacy-hero-right">
              <div className="privacy-badge">
                <span className="privacy-badge-dot"></span>
                Privacy & Security
              </div>
              <h1 className="privacy-hero-title">
                Privacy <span className="privacy-hero-highlight">Policy</span>
              </h1>
              <p className="privacy-hero-description">
                Your privacy is important to us. This Privacy Policy explains how Pick Pack Pro collects, uses, stores
                and protects your personal information when you visit our website, request a quote or use our fulfilment
                services. We handle your data securely and in line with UK data protection law.
              </p>
              <div className="privacy-hero-cta">
                <Link to="/contact-fulfillment-services" className="privacy-cta-btn privacy-cta-primary">
                  Contact Support
                </Link>
                <a
                  href="#support"
                  className="privacy-cta-btn privacy-cta-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('support');
                  }}
                >
                  Get Help
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="privacy-nav-section">
        <div className="privacy-nav-container">
          <div className="privacy-nav-links">
            <button
              onClick={() => scrollToSection('overview')}
              className={`privacy-nav-link ${activeSection === 'overview' ? 'active' : ''}`}
            >
              Overview
            </button>
            <button
              onClick={() => scrollToSection('data-collection')}
              className={`privacy-nav-link ${activeSection === 'data-collection' ? 'active' : ''}`}
            >
              Data Collection
            </button>
            <button
              onClick={() => scrollToSection('data-use')}
              className={`privacy-nav-link ${activeSection === 'data-use' ? 'active' : ''}`}
            >
              How We Use Data
            </button>
            <button
              onClick={() => scrollToSection('data-sharing')}
              className={`privacy-nav-link ${activeSection === 'data-sharing' ? 'active' : ''}`}
            >
              Data Sharing
            </button>
            <button
              onClick={() => scrollToSection('security')}
              className={`privacy-nav-link ${activeSection === 'security' ? 'active' : ''}`}
            >
              Security
            </button>
            <button
              onClick={() => scrollToSection('rights')}
              className={`privacy-nav-link ${activeSection === 'rights' ? 'active' : ''}`}
            >
              Your Rights
            </button>
            <button
              onClick={() => scrollToSection('cookies')}
              className={`privacy-nav-link ${activeSection === 'cookies' ? 'active' : ''}`}
            >
              Cookies
            </button>
            <button
              onClick={() => scrollToSection('support')}
              className={`privacy-nav-link ${activeSection === 'support' ? 'active' : ''}`}
            >
              Support
            </button>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="privacy-content-section">
        <div className="privacy-content-container">
          <div id="overview" className="privacy-section">
            <h2 className="privacy-section-title">1. Overview</h2>
            <p className="privacy-text">
              Pick Pack Pro is committed to protecting your privacy and handling your personal information responsibly.
              This Privacy Policy explains what information we collect, how we use it, who we share it with, and the
              steps we take to keep it secure when you visit our website, request a quote, or use our{' '}
              <Link to="/multi-channel-fulfillment-services" className="privacy-link">
                multi-channel fulfilment services
              </Link>
              . We process personal information in accordance with the UK General Data Protection Regulation (UK GDPR),
              the Data Protection Act 2018 and other applicable privacy legislation. By using our website or services,
              you acknowledge that your personal information will be processed as described in this Privacy Policy.
            </p>
            <p className="privacy-text">
              Learn more about who we are on our{' '}
              <Link to="/about-our-fulfillment-company" className="privacy-link">
                about our UK fulfilment company
              </Link>{' '}
              page, or explore{' '}
              <Link to="/pricing-fulfilment-services" className="privacy-link">
                fulfilment pricing UK
              </Link>{' '}
              before you share business details for a quote.
            </p>
            <p className="privacy-text">
              <strong>Last updated:</strong> August 2026
            </p>
          </div>

          <div id="data-collection" className="privacy-section">
            <h2 className="privacy-section-title">2. Information We Collect</h2>
            <p className="privacy-text">
              We collect personal information to provide our fulfilment services, respond to enquiries and improve your
              experience with Pick Pack Pro — including{' '}
              <Link to="/amazon-fba-prep-services" className="privacy-link">
                Amazon FBA prep services
              </Link>{' '}
              and related warehousing workflows.
            </p>
            <h3 className="privacy-subsection-title">2.1 Information You Provide</h3>
            <p className="privacy-text">Depending on how you interact with us, we may collect:</p>
            <ul className="privacy-list">
              <li>Your name, company name and job title</li>
              <li>Email address and telephone number</li>
              <li>Billing and delivery addresses</li>
              <li>Business information relevant to your fulfilment requirements</li>
              <li>Product, inventory and order details</li>
              <li>Enquiries submitted through our website</li>
              <li>Customer support correspondence</li>
            </ul>
            <p className="privacy-text">
              If you make a payment, payment details are processed securely by our authorised payment providers. Pick
              Pack Pro does not store full payment card details on its own systems.
            </p>

            <h3 className="privacy-subsection-title">2.2 Information Collected Automatically</h3>
            <p className="privacy-text">
              When you visit our website, certain technical information may be collected automatically, including:
            </p>
            <ul className="privacy-list">
              <li>IP address</li>
              <li>Browser and device information</li>
              <li>Operating system</li>
              <li>Website usage and navigation data</li>
              <li>Referring websites</li>
              <li>Approximate geographic location</li>
              <li>
                Cookie and analytics data — see our{' '}
                <a
                  href="#cookies"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('cookies');
                  }}
                  className="privacy-link"
                >
                  Cookies section
                </a>{' '}
                for more details
              </li>
            </ul>
            <p className="privacy-text">
              This information helps us improve website performance, security and user experience.
            </p>
          </div>

          <div id="data-use" className="privacy-section">
            <h2 className="privacy-section-title">3. How We Use Your Information</h2>
            <p className="privacy-text">
              We use your information only where there is a legitimate business reason or legal obligation to do so.
              This includes:
            </p>
            <ul className="privacy-list">
              <li>
                Providing our fulfilment and warehousing services, including{' '}
                <Link to="/logistics-services" className="privacy-link">
                  e-commerce logistics for sellers
                </Link>
              </li>
              <li>Processing customer enquiries and quotations</li>
              <li>
                Managing inventory and order fulfilment via our{' '}
                <Link to="/wms-integrations" className="privacy-link">
                  WMS integrations
                </Link>
              </li>
              <li>Sending shipment updates and service notifications</li>
              <li>Providing customer support</li>
              <li>Improving our website and services</li>
              <li>Analysing website performance</li>
              <li>Preventing fraud and maintaining security</li>
              <li>Meeting legal and regulatory obligations</li>
              <li>Sending marketing communications where you have chosen to receive them</li>
            </ul>
            <p className="privacy-text">We only use your information for the purposes for which it was collected.</p>
          </div>

          <div id="data-sharing" className="privacy-section">
            <h2 className="privacy-section-title">4. Sharing Your Information</h2>
            <p className="privacy-text">We never sell your personal information.</p>
            <p className="privacy-text">Your information may be shared only where necessary, including with:</p>
            <ul className="privacy-list">
              <li>Courier and delivery partners</li>
              <li>Payment service providers</li>
              <li>IT and cloud hosting providers</li>
              <li>Warehouse management software providers</li>
              <li>Professional advisers</li>
              <li>Regulatory authorities where legally required</li>
            </ul>
            <p className="privacy-text">
              All service providers are required to protect your information and only process it on our behalf for
              agreed purposes.
            </p>
          </div>

          <div id="security" className="privacy-section">
            <h2 className="privacy-section-title">5. Keeping Your Information Secure</h2>
            <p className="privacy-text">
              Protecting customer information is an important part of our business. We use appropriate technical and
              organisational measures to safeguard personal information, including:
            </p>
            <ul className="privacy-list">
              <li>Secure encrypted connections (SSL/TLS)</li>
              <li>Controlled access to business systems</li>
              <li>Password-protected accounts</li>
              <li>Secure cloud infrastructure</li>
              <li>Regular software updates and monitoring</li>
              <li>Staff access controls based on business need</li>
            </ul>
            <p className="privacy-text">
              Although we take every reasonable precaution, no online service can guarantee complete security. Operational
              practices at our{' '}
              <Link to="/fulfilment-centre-milton-keynes" className="privacy-link">
                Milton Keynes fulfilment centre
              </Link>{' '}
              follow the same need-to-know access approach for stock and order data.
            </p>
          </div>

          <div id="rights" className="privacy-section">
            <h2 className="privacy-section-title">6. Your Privacy Rights</h2>
            <p className="privacy-text">Under UK data protection law, you may have the right to:</p>
            <ul className="privacy-list">
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Restrict or object to certain processing</li>
              <li>Request a copy of your data</li>
              <li>Withdraw consent where applicable</li>
              <li>Lodge a complaint with the Information Commissioner&apos;s Office (ICO)</li>
            </ul>
            <p className="privacy-text">
              To exercise any of these rights, email{' '}
              <a href="mailto:privacy@pickpackpro.co.uk" className="privacy-link">
                privacy@pickpackpro.co.uk
              </a>{' '}
              or use our{' '}
              <Link to="/contact-fulfillment-services" className="privacy-link">
                contact fulfilment services
              </Link>{' '}
              form. For general service questions, see our{' '}
              <Link to="/fulfillment-frequently-asked-questions" className="privacy-link">
                fulfilment FAQs
              </Link>
              .
            </p>
          </div>

          <div id="cookies" className="privacy-section">
            <h2 className="privacy-section-title">7. Cookies</h2>
            <p className="privacy-text">
              Our website uses cookies to improve functionality, understand website usage and enhance your browsing
              experience.
            </p>
            <h3 className="privacy-subsection-title">7.1 Types of Cookies We Use</h3>
            <ul className="privacy-list">
              <li>Essential cookies</li>
              <li>Website performance cookies</li>
              <li>Analytics cookies</li>
              <li>Functional cookies</li>
              <li>Marketing cookies (where consent has been provided)</li>
            </ul>
            <p className="privacy-text">
              You can manage or disable cookies through your browser settings at any time.
            </p>
          </div>

          <div id="support" className="privacy-section privacy-support-section">
            <h2 className="privacy-section-title">8. Contact Us</h2>
            <p className="privacy-text">
              If you have any questions about this Privacy Policy or how Pick Pack Pro handles your personal information,
              please get in touch with our team. We will respond to privacy enquiries as quickly as possible and in
              accordance with applicable data protection legislation.
            </p>
            <div className="privacy-support-cards">
              <div className="privacy-support-card">
                <div className="privacy-support-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <h3 className="privacy-support-title">Email Support</h3>
                <p className="privacy-support-text">privacy@pickpackpro.co.uk</p>
                <a href="mailto:privacy@pickpackpro.co.uk" className="privacy-support-link" style={{ color: '#ffffff' }}>
                  Email us
                </a>
              </div>
              <div className="privacy-support-card">
                <div className="privacy-support-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <h3 className="privacy-support-title">Phone Support</h3>
                <p className="privacy-support-text">{BUSINESS_PHONE_DISPLAY}</p>
                <a href={BUSINESS_PHONE_TEL} className="privacy-support-link" style={{ color: '#ffffff' }}>
                  Call Now
                </a>
              </div>
              <div className="privacy-support-card">
                <div className="privacy-support-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3 className="privacy-support-title">Office Address</h3>
                <p className="privacy-support-text">{OFFICE_ADDRESS_LINE}</p>
                <Link
                  to="/contact-fulfillment-services"
                  className="privacy-support-link"
                  style={{ color: '#ffffff' }}
                >
                  Visit Contact Page
                </Link>
              </div>
            </div>
            <div className="privacy-cta-box">
              <h3 className="privacy-cta-title">Need Immediate Assistance?</h3>
              <p className="privacy-cta-text">
                Our support team is available to help you with any privacy-related questions or concerns.
              </p>
              <div className="privacy-cta-buttons">
                <Link to="/contact-fulfillment-services" className="privacy-cta-btn privacy-cta-primary">
                  Contact Support
                </Link>
                <Link to="/fulfillment-frequently-asked-questions" className="privacy-cta-btn privacy-cta-secondary">
                  View FAQs
                </Link>
              </div>
            </div>
          </div>

          <div className="privacy-section">
            <h2 className="privacy-section-title">9. Additional Information</h2>
            <h3 className="privacy-subsection-title">9.1 Children&apos;s Privacy</h3>
            <p className="privacy-text">
              Pick Pack Pro&apos;s website and fulfilment services are intended for businesses and individuals aged 18 or
              over. We do not knowingly collect or process personal information relating to children. If we become aware
              that a child has provided personal information without appropriate consent, we will take reasonable steps to
              delete it promptly.
            </p>
            <h3 className="privacy-subsection-title">9.2 International Data Transfers</h3>
            <p className="privacy-text">
              Some of our trusted service providers may process personal information outside the United Kingdom. Where
              this occurs, we ensure appropriate safeguards are in place to protect your data and comply with the
              requirements of the UK GDPR and other applicable data protection legislation.
            </p>
            <h3 className="privacy-subsection-title">9.3 Changes to This Privacy Policy</h3>
            <p className="privacy-text">
              We may update this Privacy Policy from time to time to reflect changes in our services, legal obligations,
              or business practices. Any updates will be published on this page, together with the revised &quot;Last
              Updated&quot; date. We encourage you to review this policy periodically to stay informed about how we
              protect your personal information.
            </p>
            <h3 className="privacy-subsection-title">9.4 Related Policies</h3>
            <p className="privacy-text">
              This Privacy Policy should be read alongside our{' '}
              <a
                href="#cookies"
                className="privacy-link"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('cookies');
                }}
              >
                Cookies
              </a>{' '}
              details above and other notices on the Pick Pack Pro website, including our{' '}
              <Link to="/fulfillment-blog" className="privacy-link">
                fulfilment blog UK
              </Link>{' '}
              for service updates that may affect how we operate. Together, these explain how we collect, use, protect
              and manage your personal information when you interact with our website and fulfilment services.
            </p>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
